import { API_BASE_URL } from "./api";
import { ApiLoginResponse, AuthResponse, AuthServiceError, LoginCredentials } from "../types/auth";
import { isAdminAllowedRole, ADMIN_MODULE_ID } from "../utils/authRole";
import { buildAppPath } from "../config/appBasePath";

const TOKEN_KEY = "admin_access_token";
const USER_KEY = "admin_user_data";

class AuthApiError extends Error implements AuthServiceError {
    status?: number;

    constructor(message: string, status?: number) {
        super(message);
        this.name = "AuthApiError";
        this.status = status;
    }
}

/**
 * Map the raw backend login response into the normalised shape
 * used across the admin frontend.
 */
function mapLoginResponse(raw: ApiLoginResponse): AuthResponse {
    const user = raw.user;

    // If the modules list contains the admin module, tag as "Admin"
    const tokenRole = isAdminAllowedRole(user.modules) ? "Admin" : undefined;

    return {
        accessToken: raw.tokenValue,
        userId: String(user.id),
        username: user.username,
        fullname: user.fullname,
        modules: user.modules,
        tokenRole,
    };
}

function ensureAdminRole(response: AuthResponse): AuthResponse {
    if (!isAdminAllowedRole(response.tokenRole ?? null)) {
        throw new AuthApiError("Your account is not authorized for the Admin panel.", 403);
    }

    return response;
}

export const authService = {
    getAccessToken: () => localStorage.getItem(TOKEN_KEY),

    getUserData: (): AuthResponse | null => {
        const data = localStorage.getItem(USER_KEY);
        if (!data) {
            return null;
        }

        try {
            return JSON.parse(data) as AuthResponse;
        } catch {
            return null;
        }
    },

    /** Get the stored user or throw – use when you know the user is logged in. */
    getUser: (): AuthResponse => {
        const user = authService.getUserData();
        if (!user) throw new AuthApiError("Not logged in.", 401);
        return user;
    },

    /** Shortcut: returns the logged-in user's ID (throws if not logged in). */
    getUserId: (): string => authService.getUser().userId,

    setSession: (response: AuthResponse) => {
        localStorage.setItem(TOKEN_KEY, response.accessToken);
        localStorage.setItem(USER_KEY, JSON.stringify(response));
    },

    clearSession: () => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
    },

    login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
        const res = await fetch(`${API_BASE_URL}/Login/Login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                username: credentials.username,
                password: credentials.password,
                module: ADMIN_MODULE_ID,
            }),
            credentials: "include"
        });

        if (!res.ok) {
            const fallbackMessage = res.status === 404
                ? "Invalid username or password."
                : res.status === 403
                    ? "Your account is not authorized for the Admin panel."
                    : "Login failed";

            const payload = await res.json().catch(() => ({}));
            throw new AuthApiError(payload.message || fallbackMessage, res.status);
        }

        const raw: ApiLoginResponse = await res.json();
        const data = ensureAdminRole(mapLoginResponse(raw));
        authService.setSession(data);
        return data;
    },

    logout: async () => {
        authService.clearSession();
        window.location.href = buildAppPath("/login");
    },

    /**
     * The new backend does not support refresh tokens.
     * If called, clear the session and redirect to login.
     */
    refreshToken: async (): Promise<AuthResponse> => {
        authService.clearSession();
        window.location.href = buildAppPath("/login");
        throw new Error("Session expired. Please log in again.");
    }
};
